import { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  createMapNode,
  kindLabel,
  layerLabel,
  MAP_NODE_KINDS,
  MAP_NODE_LAYERS,
  slugifyMapNodeLabel,
  uniqueMapNodeSlug,
  type MapNodeKind,
  type MapNodeLayer,
  type MapNodeRow,
} from '../../lib/systemMap';

export interface SystemMapAddNodeDialogProps {
  open: boolean;
  mapId: string;
  existingSlugs: string[];
  defaultX: number;
  defaultY: number;
  onClose: () => void;
  onCreated: (node: MapNodeRow) => void;
}

export default function SystemMapAddNodeDialog({
  open,
  mapId,
  existingSlugs,
  defaultX,
  defaultY,
  onClose,
  onCreated,
}: SystemMapAddNodeDialogProps) {
  const [label, setLabel] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [kind, setKind] = useState<MapNodeKind>('workflow');
  const [layer, setLayer] = useState<MapNodeLayer>('public');
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLabel('');
    setSlug('');
    setSlugTouched(false);
    setKind('workflow');
    setLayer('public');
    setUrl('');
  }, [open]);

  const suggestedSlug = useMemo(() => slugifyMapNodeLabel(label), [label]);

  useEffect(() => {
    if (!slugTouched) setSlug(suggestedSlug);
  }, [suggestedSlug, slugTouched]);

  if (!open) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      toast.error('Label is required');
      return;
    }

    const baseSlug = slug.trim() || suggestedSlug;
    if (!baseSlug) {
      toast.error('Slug is required');
      return;
    }

    const finalSlug = uniqueMapNodeSlug(baseSlug, existingSlugs);

    setSaving(true);
    try {
      const node = await createMapNode({
        mapId,
        label: trimmedLabel,
        slug: finalSlug,
        kind,
        layer,
        url: url.trim() || null,
        x: defaultX,
        y: defaultY,
      });
      onCreated(node);
      toast.success('Node added');
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to add node');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4 bg-black/40">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-node-title"
        className="w-full max-w-md rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl"
      >
        <form onSubmit={(e) => void handleSubmit(e)} className="p-5 space-y-4">
          <div>
            <h2 id="add-node-title" className="text-base font-semibold text-gray-900 dark:text-white">
              Add node
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Placed near your current view. Drag it into position after creating.
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              Label
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Outgoing newsletters"
              autoFocus
              className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              Slug
            </label>
            <input
              type="text"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
              placeholder="auto-from-label"
              className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                Type
              </label>
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as MapNodeKind)}
                className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {MAP_NODE_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {kindLabel(k)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                Layer
              </label>
              <select
                value={layer}
                onChange={(e) => setLayer(e.target.value as MapNodeLayer)}
                className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {MAP_NODE_LAYERS.map((l) => (
                  <option key={l} value={l}>
                    {layerLabel(l)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              URL (optional)
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…"
              className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-3 py-2 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Add node
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
